/**
 * DeepAnalysisCard - Tier 2 LLM-Powered Semantic Analysis Display
 *
 * Displays semantic analysis results including:
 * - Domain/range suggestions
 * - Intermediate category suggestions
 * - Upper ontology mappings
 * - Missing relationships
 * - Regulatory alignments
 *
 * Includes consent warning and explicit trigger button.
 *
 * @version 1.0.0
 * @date 2026-02-04
 */

import React from 'react';
import { ImprovementRecommendation, type RecommendationSeverity } from './ImprovementRecommendation';

// =============================================================================
// TYPES
// =============================================================================

interface DomainRangeSuggestion {
  propertyUri: string;
  propertyLabel: string;
  suggestedDomain: string[];
  suggestedRange: string[];
  confidence: 'high' | 'medium' | 'low';
  rationale: string;
}

interface IntermediateCategory {
  suggestedName: string;
  suggestedUri: string;
  definition: string;
  childClasses: string[];
  rationale: string;
}

interface UpperOntologyMapping {
  sourceClass: string;
  targetOntology: 'BFO' | 'IAO' | 'OBI' | 'PROV-O' | 'DCAT';
  targetClass: string;
  mappingType: 'equivalent' | 'subclass' | 'related';
  confidence: 'high' | 'medium' | 'low';
  rationale: string;
}

interface MissingRelationship {
  suggestedProperty: string;
  domain: string;
  range: string;
  rationale: string;
}

interface RegulatoryAlignment {
  ontologyClass: string;
  regulation: string;
  section: string;
  relevance: string;
}

export interface SemanticAnalysis {
  domainRangeSuggestions: DomainRangeSuggestion[];
  intermediateCategorySuggestions: IntermediateCategory[];
  upperOntologyMappings: UpperOntologyMapping[];
  missingRelationships: MissingRelationship[];
  regulatoryAlignments: RegulatoryAlignment[];
  overallAssessment: string;
  analyzedAt: string;
}

interface DeepAnalysisCardProps {
  analysis: SemanticAnalysis | null;
  loading: boolean;
  error: string | null;
  onRunAnalysis: () => void;
  aiAvailable: boolean;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const getSeverityFromIssueType = (
  type: 'domain-range' | 'hierarchy' | 'upper-ontology' | 'relationship' | 'regulatory'
): RecommendationSeverity => {
  switch (type) {
    case 'domain-range':
      return 'critical';
    case 'hierarchy':
      return 'high';
    case 'upper-ontology':
      return 'medium';
    case 'relationship':
      return 'high';
    case 'regulatory':
      return 'medium';
    default:
      return 'low';
  }
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const DeepAnalysisCard: React.FC<DeepAnalysisCardProps> = ({
  analysis,
  loading,
  error,
  onRunAnalysis,
  aiAvailable,
  usage,
}) => {
  // Pre-analysis state (show run button)
  if (!analysis && !loading && !error) {
    return (
      <div className="deep-analysis-card deep-analysis-pending">
        <div className="deep-analysis-header">
          <span className="deep-analysis-icon">🤖</span>
          <h3>AI-Powered Deep Analysis</h3>
        </div>

        <div className="deep-analysis-intro">
          <p>Get semantic recommendations for:</p>
          <ul>
            <li>Missing domain/range declarations for properties</li>
            <li>Intermediate categories to improve hierarchy</li>
            <li>Upper ontology alignment (BFO, IAO, OBI)</li>
            <li>Missing relationship suggestions</li>
            <li>Regulatory framework mappings</li>
          </ul>
        </div>

        <div className="deep-analysis-warning">
          <span className="warning-icon">⚠️</span>
          <span>This sends ontology structure to the configured AI provider for analysis</span>
        </div>

        <button
          className="btn btn-primary deep-analysis-run-btn"
          onClick={onRunAnalysis}
          disabled={!aiAvailable}
        >
          🔍 Run Deep Analysis
        </button>

        {!aiAvailable && (
          <p className="deep-analysis-unavailable">
            AI provider is not configured. Contact your administrator to enable deep analysis.
          </p>
        )}
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="deep-analysis-card deep-analysis-loading">
        <div className="deep-analysis-header">
          <span className="deep-analysis-icon">🤖</span>
          <h3>AI-Powered Deep Analysis</h3>
        </div>
        <div className="deep-analysis-loading-content">
          <div className="spinner" />
          <p>Analyzing ontology semantics...</p>
          <p className="loading-hint">This may take 30-60 seconds for large ontologies.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="deep-analysis-card deep-analysis-error">
        <div className="deep-analysis-header">
          <span className="deep-analysis-icon">🤖</span>
          <h3>AI-Powered Deep Analysis</h3>
        </div>
        <div className="deep-analysis-error-content">
          <span className="error-icon">❌</span>
          <p>{error}</p>
          <button
            className="btn btn-secondary"
            onClick={onRunAnalysis}
            disabled={!aiAvailable}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Analysis results
  if (!analysis) {
    return null;
  }

  const {
    domainRangeSuggestions,
    intermediateCategorySuggestions,
    upperOntologyMappings,
    missingRelationships,
    regulatoryAlignments,
    overallAssessment,
    analyzedAt,
  } = analysis;

  return (
    <div className="deep-analysis-card deep-analysis-results">
      {/* Header */}
      <div className="deep-analysis-header">
        <span className="deep-analysis-icon">🤖</span>
        <h3>AI-Powered Deep Analysis</h3>
        <span className="analyzed-date">
          Analyzed: {new Date(analyzedAt).toLocaleString()}
        </span>
      </div>

      {/* Overall Assessment */}
      <div className="overall-assessment">
        <p>{overallAssessment}</p>
      </div>

      {/* Domain/Range Suggestions */}
      {domainRangeSuggestions.length > 0 && (
        <ImprovementRecommendation
          severity={getSeverityFromIssueType('domain-range')}
          title="Properties Missing Domain/Range"
          description={`${domainRangeSuggestions.length} properties have no domain or range declarations. This prevents reasoning and limits graph traversal.`}
          items={domainRangeSuggestions.map((s) => ({
            label: s.propertyLabel,
            detail: `domain=${s.suggestedDomain.join('|')}, range=${s.suggestedRange.join('|')}`,
            confidence: s.confidence,
            rationale: s.rationale,
          }))}
          maxItems={5}
        />
      )}

      {/* Intermediate Categories */}
      {intermediateCategorySuggestions.length > 0 && (
        <ImprovementRecommendation
          severity={getSeverityFromIssueType('hierarchy')}
          title="Missing Intermediate Categories"
          description="The hierarchy is flat. These intermediate categories would improve organization and navigation."
          items={intermediateCategorySuggestions.map((s) => ({
            label: s.suggestedName,
            detail: `Groups: ${s.childClasses.slice(0, 3).join(', ')}${
              s.childClasses.length > 3 ? ` +${s.childClasses.length - 3} more` : ''
            }`,
            rationale: s.rationale,
          }))}
          maxItems={5}
        />
      )}

      {/* Upper Ontology Mappings */}
      {upperOntologyMappings.length > 0 && (
        <ImprovementRecommendation
          severity={getSeverityFromIssueType('upper-ontology')}
          title="Upper Ontology Alignment"
          description="Suggested alignments to standard upper ontologies for improved interoperability."
          items={upperOntologyMappings.map((s) => ({
            label: s.sourceClass,
            detail: `${s.mappingType} to ${s.targetOntology}:${s.targetClass}`,
            confidence: s.confidence,
            rationale: s.rationale,
          }))}
          maxItems={5}
        />
      )}

      {/* Missing Relationships */}
      {missingRelationships.length > 0 && (
        <ImprovementRecommendation
          severity={getSeverityFromIssueType('relationship')}
          title="Missing Relationships"
          description="Suggested relationships that appear to be missing from the ontology."
          items={missingRelationships.map((s) => ({
            label: s.suggestedProperty,
            detail: `${s.domain} → ${s.range}`,
            rationale: s.rationale,
          }))}
          maxItems={5}
        />
      )}

      {/* Regulatory Alignments */}
      {regulatoryAlignments.length > 0 && (
        <ImprovementRecommendation
          severity={getSeverityFromIssueType('regulatory')}
          title="Regulatory Framework Alignments"
          description="Suggested mappings to regulatory frameworks for compliance documentation."
          items={regulatoryAlignments.map((s) => ({
            label: s.ontologyClass,
            detail: `${s.regulation} - ${s.section}`,
            rationale: s.relevance,
          }))}
          maxItems={5}
        />
      )}

      {/* Footer with actions */}
      <div className="deep-analysis-footer">
        <button
          className="btn btn-secondary"
          onClick={onRunAnalysis}
          disabled={loading}
        >
          ↻ Re-run Analysis
        </button>

        {usage && (
          <span className="token-usage">
            Tokens used: {usage.totalTokens.toLocaleString()} (
            {usage.inputTokens.toLocaleString()} in, {usage.outputTokens.toLocaleString()} out)
          </span>
        )}
      </div>
    </div>
  );
};

export default DeepAnalysisCard;
